import { MaterialResources } from "@/state/MaterialResources";
import React, { useEffect, useMemo } from "react";
import { ModelInstance } from "../modelInstance/ModelInstance";
import { MeshInstance } from "../modelInstance/MeshInstance";
import { ElementInstance } from "../element/Element";
import { getMeshMaterialRenderData } from "./getMeshMaterialRenderData";
import * as THREE from "three";

type Props = {
    model: ModelInstance;
    instance: MeshInstance | ElementInstance;
    materials: Record<string, MaterialResources>;
    isSelected?: boolean;
    selectedMeshMaterialName?: string;
};

export const MeshMaterials: React.FC<Props> = ({
    model,
    instance,
    materials,
    selectedMeshMaterialName,
}) => {
    const meshMaterials = useMemo(() => {
        return getMeshMaterialRenderData(model, instance, materials);
    }, [model.materialSlotMapping, model.config.materialSlots, materials]);

    useEffect(() => {
        meshMaterials.forEach((data) => {
            if (!data.textures.isCommon && data.textures.normal) {
                data.textures.normal.colorSpace = THREE.NoColorSpace;
            }
        });
    }, [meshMaterials]);

    return (
        <>
            {meshMaterials.length > 0 ? (
                meshMaterials.map((data, index) => {
                    const config: THREE.MeshPhysicalMaterialParameters = {
                        map: data.textures.color,
                        normalMap: data.textures.normal,
                        normalMapType: THREE.TangentSpaceNormalMap,
                        roughnessMap: data.textures.roughness,
                        bumpScale: data.bumpScale,
                        metalnessMap: data.textures.metalness,
                        color:
                            selectedMeshMaterialName === data.meshMaterialName
                                ? "red"
                                : data.color ?? "white",
                        emissive: data.emissiveColor,
                        emissiveIntensity: data.emissiveIntensity,
                        flatShading: !data.smoothing,
                        transmission: data.transparency ?? 0,
                        roughness: data.roughness,
                        metalness: data.metalness,
                        clearcoat: data.clearcoatThickness,
                        clearcoatRoughness: data.clearcoatRoughness,
                        anisotropy: data.anisotropy,
                        anisotropyRotation: data.anisotropyRotation,
                        specularIntensity: 1,
                    };

                    return (
                        <meshPhysicalMaterial
                            attach={`material-${index}`}
                            key={index}
                            {...config}
                        />
                    );
                })
            ) : (
                <meshBasicMaterial color="red" wireframe={true} />
            )}
        </>
    );
};
